<template>
  <el-dialog
    :visible.sync="innerVisible"
    title="订单退款"
    width="520px"
    append-to-body
    :close-on-click-modal="false"
    custom-class="xw-refund-dialog"
    @closed="onClosed"
  >
    <div class="xw-refund-body">
      <div class="xw-refund-order">
        <div class="xw-refund-order__row">
          <span class="label">订单号</span>
          <span class="value">{{ orderNo || "—" }}</span>
        </div>
        <div v-if="orderTypeLabel" class="xw-refund-order__row">
          <span class="label">订单类型</span>
          <span class="value">{{ orderTypeLabel }}</span>
        </div>
        <div class="xw-refund-order__amounts">
          <template v-if="showAmountBreakdown">
            <div class="amount-item">
              <span class="amount-label">鉴别/服务费</span>
              <span class="amount-value">¥{{ formatMoney(displayServicePrice) }}</span>
            </div>
            <div class="amount-item">
              <span class="amount-label">寄件运费</span>
              <span class="amount-value">¥{{ formatMoney(displayFreight) }}</span>
            </div>
          </template>
          <div class="amount-item" :class="{ highlight: !showAmountBreakdown }">
            <span class="amount-label">实付金额</span>
            <span class="amount-value">¥{{ formatMoney(displayPaid) }}</span>
          </div>
          <div class="amount-item">
            <span class="amount-label">已退金额</span>
            <span class="amount-value muted">¥{{ formatMoney(displayRefunded) }}</span>
          </div>
          <div class="amount-item highlight">
            <span class="amount-label">可退余额</span>
            <span class="amount-value">¥{{ formatMoney(remainingAmount) }}</span>
          </div>
        </div>
        <div v-if="displayFreight > 0" class="amount-breakdown-tip">
          实付金额含用户支付的寄件运费，退款时将一并计入可退上限
        </div>
      </div>

      <el-form label-width="88px" size="small" @submit.native.prevent>
        <el-form-item label="退款金额" required>
          <el-input
            v-model.trim="form.refundAmount"
            placeholder="请输入退款金额"
            @blur="normalizeAmount"
          >
            <template slot="append">元</template>
          </el-input>
          <div class="field-tip">
            本次最多可退 ¥{{ formatMoney(remainingAmount) }}
            <el-button v-if="remainingAmount > 0" type="text" @click="fillRemaining">全额填入</el-button>
          </div>
        </el-form-item>

        <el-form-item label="退款方式" required>
          <div class="mode-cards">
            <div
              class="mode-card"
              :class="{ active: form.closeOrder === false }"
              @click="form.closeOrder = false"
            >
              <div class="mode-card__title">仅退款</div>
              <div class="mode-card__desc">只退钱，订单继续履约</div>
              <div class="mode-card__hint">适用于防伪码寄售退鉴别费等场景</div>
            </div>
            <div
              class="mode-card"
              :class="{ active: form.closeOrder === true }"
              @click="form.closeOrder = true"
            >
              <div class="mode-card__title">关单退款</div>
              <div class="mode-card__desc">退款并关闭订单</div>
              <div class="mode-card__hint">订单状态变为已退款，不可继续履约</div>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="退款原因">
          <el-input
            v-model.trim="form.refundReason"
            type="textarea"
            :rows="2"
            maxlength="200"
            show-word-limit
            placeholder="选填，默认按退款方式自动生成"
          />
        </el-form-item>
      </el-form>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleCancel">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="handleConfirm">确认退款</el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: "XwOrderRefundDialog",
  props: {
    visible: { type: Boolean, default: false },
    orderNo: { type: String, default: "" },
    orderTypeLabel: { type: String, default: "" },
    refundAmount: { type: [Number, String], default: null },
    servicePrice: { type: [Number, String], default: null },
    freightAmount: { type: [Number, String], default: null },
    paidAmount: { type: [Number, String], default: null },
    refundedAmount: { type: [Number, String], default: null },
  },
  data() {
    return {
      innerVisible: false,
      submitting: false,
      confirmed: false,
      form: {
        refundAmount: "",
        closeOrder: true,
        refundReason: "",
      },
    };
  },
  computed: {
    displayFreight() {
      return this.toNumber(this.freightAmount) || 0;
    },
    displayServicePrice() {
      const service = this.toNumber(this.servicePrice);
      if (service != null) return service;
      return Math.max(0, this.displayPaid - this.displayFreight);
    },
    showAmountBreakdown() {
      return this.displayFreight > 0;
    },
    displayPaid() {
      const paid = this.toNumber(this.paidAmount);
      if (paid != null) return paid;
      return this.toNumber(this.refundAmount) || 0;
    },
    displayRefunded() {
      return this.toNumber(this.refundedAmount) || 0;
    },
    remainingAmount() {
      const remain = this.displayPaid - this.displayRefunded;
      return remain > 0 ? Number(remain.toFixed(2)) : 0;
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(v) {
        this.innerVisible = v;
        if (v) this.resetForm();
      },
    },
    innerVisible(v) {
      this.$emit("update:visible", v);
    },
  },
  methods: {
    toNumber(v) {
      if (v == null || v === "") return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    },
    formatMoney(v) {
      const n = this.toNumber(v);
      return n == null ? "0.00" : n.toFixed(2);
    },
    resetForm() {
      this.confirmed = false;
      const defaultAmt = this.remainingAmount > 0
        ? this.remainingAmount
        : (this.toNumber(this.refundAmount) || 0);
      this.form = {
        refundAmount: defaultAmt > 0 ? String(defaultAmt) : "",
        closeOrder: true,
        refundReason: "",
      };
      this.submitting = false;
    },
    fillRemaining() {
      this.form.refundAmount = String(this.remainingAmount);
    },
    normalizeAmount() {
      const n = this.toNumber(this.form.refundAmount);
      if (n == null) return;
      this.form.refundAmount = String(Number(n.toFixed(2)));
    },
    validate() {
      const amount = this.toNumber(this.form.refundAmount);
      if (amount == null || amount <= 0) {
        this.$message.warning("请输入有效的退款金额");
        return null;
      }
      if (this.remainingAmount > 0 && amount > this.remainingAmount + 0.001) {
        this.$message.warning(`退款金额不能超过可退余额 ¥${this.formatMoney(this.remainingAmount)}`);
        return null;
      }
      if (this.form.closeOrder !== true && this.form.closeOrder !== false) {
        this.$message.warning("请选择退款方式");
        return null;
      }
      return {
        refundAmount: Number(amount.toFixed(2)),
        closeOrder: this.form.closeOrder,
        refundReason: this.form.refundReason || undefined,
      };
    },
    handleConfirm() {
      const payload = this.validate();
      if (!payload) return;
      this.submitting = true;
      this.confirmed = true;
      this.innerVisible = false;
      this.$emit("confirm", payload);
    },
    handleCancel() {
      this.innerVisible = false;
    },
    onClosed() {
      this.submitting = false;
      if (!this.confirmed) {
        this.$emit("cancel");
      }
    },
  },
};
</script>

<style lang="less">
.xw-refund-dialog {
  .el-dialog__body {
    padding-top: 8px;
  }
}
.xw-refund-body {
  .xw-refund-order {
    background: #f8fafc;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 16px;
  }
  .xw-refund-order__row {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-size: 13px;
    .label {
      width: 72px;
      color: #909399;
      flex-shrink: 0;
    }
    .value {
      color: #303133;
      word-break: break-all;
    }
  }
  .xw-refund-order__amounts {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 4px;
    .amount-item {
      flex: 1 1 calc(33.33% - 10px);
      min-width: 120px;
      background: #fff;
      border-radius: 6px;
      padding: 8px 10px;
      border: 1px solid #ebeef5;
      &.highlight {
        border-color: #c6e2ff;
        background: #ecf5ff;
        .amount-value {
          color: #409eff;
        }
      }
    }
    .amount-label {
      display: block;
      font-size: 12px;
      color: #909399;
      margin-bottom: 4px;
    }
    .amount-value {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
      &.muted {
        color: #909399;
        font-weight: 500;
      }
    }
  }
  .amount-breakdown-tip {
    margin-top: 8px;
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
  }
  .field-tip {
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
  }
  .mode-cards {
    display: flex;
    gap: 10px;
  }
  .mode-card {
    flex: 1;
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    padding: 10px 12px;
    cursor: pointer;
    transition: all 0.15s ease;
    background: #fff;
    &:hover {
      border-color: #c6e2ff;
    }
    &.active {
      border-color: #409eff;
      background: #ecf5ff;
      box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.15);
    }
    &__title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 4px;
    }
    &__desc {
      font-size: 12px;
      color: #606266;
      line-height: 1.4;
    }
    &__hint {
      margin-top: 6px;
      font-size: 11px;
      color: #909399;
      line-height: 1.4;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/XwOrderRefundDialog.vue